import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal } from "@/common/components/modal";
import Table from "@/common/components/Table";
import { Pagination } from "@/common/components/pagination";
import {
  PRODUCT_URL,
  INVENTORY_URL,
  INVENTORY_PRODUCTS_URL,
  INVENTORY_STATS_URL,
  BATCH_INVENTORY_URL,
  BATCH_INVENTORY_VALIDATE_URL,
} from "@/common/constants";
import {
  ModalFormWrapper,
  ModalWideWrapper,
  ModalWrapper,
} from "@/common/elements";
import { KeyValueObj } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useAuthDetails } from "@/hooks/useAuthDetails";
import useAxiosCall from "@/hooks/useAxiosCall";
import { Barchart } from "../common/Barchart";
import { barOptions } from "../common/Data";
import {
  getBarchartData,
  getCardDetails,
  getRecentOrderTableData,
  ICardDetails,
  InventoryFilterOptions,
  InventorySortFields,
  productInventoryTableHeaders,
  productInventoryTableKeys,
  tableHeadersShort,
  tableKeysShort,
} from "./subs/data";
import { AddInventoryWrapper } from "./subs/style";
import { StatCard } from "./subs/StatCard";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import { Button } from "@/common/components/button";
import { formatToNaira, toParamsFormat } from "@/common/utils/general";
import { downloadBase64File } from "@/common/utils";
import { QuantityCell } from "@/common/components/quantity-cell";
import useQuery from "@/hooks/useQuery";
import { PageSkeletonLoader } from "@/common/components/skeleton/page-skeleton";
import { ActionPopup } from "@/common/components/action-popup";
import { usePositioning } from "@/hooks/usePositioning";
import { ProductFactory } from "../common/product-factory";
import FileUpload from "./batch/fileUploader";
import PreviewInventory from "./batch/preview";
import { Select } from "@/common/components/selectInput";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import { InventoryStep1 } from "./subs/InventoryStep1";
import { InventoryStep2 } from "./subs/InventoryStep2";
import { IInventoryNode } from "./subs/inventoryTree";
import { toSubmitTree } from "./subs/serialAccelerators";
import { SellingLevelPriceModal } from "./subs/SellingLevelPriceModal";
import {
  EMPTY_INTAKE_PRICING,
  IIntakePricing,
  PricingDetails,
} from "./subs/PricingDetails";
import { RepriceConfirm } from "./subs/RepriceConfirm";

export interface IAddInventoryProps {}
export interface IInventoryHierarchy {
  level: string;
  packaging_type: string;
  containment_quantity: number;
}


// MED-659 (UC-LST-02): the discreet "1 batch discounted" indicator sits beside
// the batch count rather than in its own column.
function buildBatchLabel(row: KeyValueObj): string {
  const batches = row?.batch_count ?? 0;
  const discounted = row?.discounted_batch_count ?? 0;
  return discounted > 0
    ? `${batches} (${discounted} discounted)`
    : String(batches);
}

export function AddInventory(_: IAddInventoryProps) {
  //STATES
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubtitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [stage, setStage] = useState("inventory-step1");
  const [retry, setRetry] = useState(0);
  const [inventories, setInventories] = useState<KeyValueObj[]>([]);
  // MED-659 (UC-LST-01): the Selling Level & Price module, opened from the row
  // action menu and (skippably) after a completed Scan-to-Receive.
  const [pricingProduct, setPricingProduct] = useState<KeyValueObj | null>(
    null,
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [, setFormValues] = useState<KeyValueObj>({});
  const [tableData, setTableData] = useState<any[]>([]);
  const [showInventoryOptions, setShowInventoryOptions] = useState(false);
  const [productType, setProductType] = useState("");
  const [addMore, setAddMore] = useState(false);
  const [openBatchUpload, setOpenBatchUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // MED-686: the bulk flow is form -> preview -> (validate) -> pricing. Pricing
  // is only reachable once the file has passed validation, so a bad file never
  // gets as far as asking for money.
  const [step, setStep] = useState<"form" | "preview" | "pricing">("form");
  const [batchPricing, setBatchPricing] =
    useState<IIntakePricing>(EMPTY_INTAKE_PRICING);
  const [batchValidation, setBatchValidation] = useState<KeyValueObj | null>(
    null,
  );
  const [rowErrors, setRowErrors] = useState<KeyValueObj | null>(null);
  const [validating, setValidating] = useState(false);
  // The server's 409 reprice refusal, plus which door raised it so the
  // confirmation re-submits through the right one.
  const [reprice, setReprice] = useState<KeyValueObj | null>(null);
  // MED-646 serialization: per-product template download + header-mismatch confirm
  const [templateLoading, setTemplateLoading] = useState(false);
  const [headerMismatch, setHeaderMismatch] = useState<KeyValueObj | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [searching, setSearching] = useState(false);
  const [listError, setListError] = useState(false);
  const [pagCount, setPagCount] = useState(1);
  // MED-719: sort/filter/search state, combined into one param set on every
  // fetch so none of the three resets the others, and so paging keeps
  // whichever of them is active.
  const [sort, setSort] = useState<{
    sortBy: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [activeFilters, setActiveFilters] = useState<KeyValueObj>({});
  const [searchTerm, setSearchTerm] = useState("");

  // MED-649: complete-batch single-upload flow (stages "inventory-step1"/"inventory-step2")
  const [inventoryProduct, setInventoryProduct] = useState<KeyValueObj | null>(
    null,
  );
  const [inventoryStep1Values, setInventoryStep1Values] = useState<KeyValueObj>(
    {},
  );
  const [inventorySubmitLoading, setInventorySubmitLoading] = useState(false);
  // MED-686: captured in Step 1 alongside the batch details, submitted with them.
  const [inventoryPricing, setInventoryPricing] =
    useState<IIntakePricing>(EMPTY_INTAKE_PRICING);
  const [serverErrorBySerial, setServerErrorBySerial] = useState<
    Record<string, string>
  >({});

  // ===== HOOKES =====
  const { data: inventoryMetrics, loading } = useQuery(INVENTORY_STATS_URL);
  const apiCall = useApiCallHandling();
  const [searchParams] = useSearchParams();
  const { companyType, hasFullAccess } = useAuthDetails();
  // ********* Hooks ***********
  const [setPosition, position] = usePositioning();

  const [BatchformData, setBatchFormData] = useState<KeyValueObj>({});

  const [getProducts, productsFetched, productsError, productsLoading] =
    useAxiosCall();
  const notify = useNotification();
  const navigate = useNavigate();

  const productOptions = useMemo<KeyValueObj[]>(() => {
    return (
      productsFetched?.map((product: KeyValueObj) => ({
        id: product.id,
        title: `${product.atc_code?.readable_name}(${product.brand_name}) - ${
          product?.strength
        }${product?.strength_unit === "grams" ? "mg" : "g"}`,
      })) ?? []
    );
  }, [productsFetched]);

  //FETCHING PAGE LOAD DATA
  // MED-688: the approved-products list (for the Add Inventory dropdowns) was
  // tied to the same `retry` counter as the inventory table, so it re-fetched
  // after every price save / batch upload / retry click even though none of
  // those change which products are approved. It only needs to load once.
  useEffect(() => {
    getProducts({
      method: "GET",
      url: PRODUCT_URL + "?status=approved",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MED-719: the single fetch path for this list — see updateTableData/the
  // effect below that drives it, once those are defined.

  // MED-698 — whether this seller's selling level is derived from the product's
  // packaging hierarchy rather than chosen (a manufacturer). Sent on every row and
  // identical across them, because the server derives it from the SELLER, not from
  // the row — so the first row that carries it answers for the whole grid.
  //
  // `some` rather than `[0]`: an admin's rows aggregate every owner's stock and
  // carry `false`, and a grid can be empty, so a bare index would read `undefined`
  // and coerce the wrong way round for nobody's benefit.
  const sellingLevelIsDerived = inventories.some(
    (row: KeyValueObj) => row?.selling_level_derived === true,
  );

  // MED-659: the row carries only the product id, so look the row back up for
  // the name the module displays.
  function openPricingModal(productId: string) {
    const row = inventories.find((item) => item?.product_id === productId);
    setPricingProduct({
      product_id: productId,
      name: row?.product_brand_name || row?.product_name || "",
    });
  }

  // Re-fetch rather than patching the row locally: repricing changes Unit, Qty
  // AND Selling Price together (a level change re-derives all three), so a
  // partial local update would leave the row internally inconsistent.
  function handlePriceSaved() {
    setPricingProduct(null);
    setRetry((prev) => prev + 1);
  }

  // MED-652: one row per product, aggregated across that product's batches.
  // MED-659 (UC-INV-02): Unit, Qty and Selling Price now render at the holder's
  // selling level. Every figure below is derived server-side and arrives ready
  // to display — nothing is recomputed here.
  useEffect(() => {
    const inventoryTable =
      inventories.length > 0 &&
      inventories.map((product: KeyValueObj) => ({
        product_name: {
          value: product?.product_brand_name || product?.product_name,
          type: "string",
        },
        // MED-681: a product currently awaiting admin approval (i.e. an
        // approved product whose latest edit re-triggered review) gets a
        // yellow "Pending Approval" badge; otherwise a blue "Approved" badge.
        // `statusType` must be a class style.ts actually defines (pending,
        // rejected/declined, accepted, shipped, delivered) — "approved" isn't
        // one, so "accepted" is reused for the blue styling.
        status:
          product?.approval_status === "pending"
            ? {
                value: "Pending Approval",
                type: "status",
                statusType: "pending",
              }
            : {
                value: "Approved",
                type: "status",
                statusType: "accepted",
              },
        sku: {
          value: product?.sku,
          type: "string",
        },
        unit: {
          // The packaging type of the selling level, not the product form.
          value: product?.selling_packaging_type || "—",
          type: "string",
        },
        strength: {
          value: product?.strength
            ? `${product.strength}${product?.strength_unit === "grams" ? "g" : "mg"}`
            : "—",
          type: "string",
        },
        storage: {
          value: product?.storage_requirement || "—",
          type: "string",
        },
        quantity: {
          // MED-697: a component, not a string — the cell owns its own expander.
          // Table renders a `string` cell's value verbatim, and a ReactNode is a
          // valid value, so this needs no change to the shared component.
          value: (
            <QuantityCell
              display={product?.quantity_display}
              fallbackBaseUnits={product?.total_quantity ?? 0}
            />
          ),
          type: "string",
        },
        batch_count: {
          value: buildBatchLabel(product),
          type: "string",
        },
        min_level: {
          value: product?.reorder_level ?? 0,
          type: "string",
        },
        supply: {
          // MED-697: sold stock reads at the selling level too. A row showing
          // "4 Cartons" in stock beside a bare "22" sold is the same confusion
          // one column over.
          value: (
            <QuantityCell
              display={product?.quantity_sold_display}
              fallbackBaseUnits={product?.quantity_sold ?? 0}
            />
          ),
          type: "string",
        },
        selling_price: {
          // "Not set", never ₦0 — an unpriced product is gated from buyers and
          // must read as unpriced to its holder too. Checked as a NUMBER: the
          // price arrives as a string, and "0" / "0.0" are both truthy, so a
          // truthiness test would have rendered ₦0 for exactly the case this
          // column exists to distinguish.
          value:
            product?.priced && Number(product?.selling_price) > 0
              ? formatToNaira(Number(product.selling_price))
              : "Not set",
          type: "string",
        },
        id: product?.product_id,
      }));

    inventoryTable && setTableData(inventoryTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventories]);

  useEffect(() => {
    if (searchParams.get("new") === "true" && !productsLoading) {
      setShowModal(true);
      setModalTitle("Add Inventory");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsLoading]);

  // ================ handling search, filters, sort and paging ============
  const INVENTORIES_URL = INVENTORY_PRODUCTS_URL;

  // MED-719: every piece that can narrow/order the list, combined into one
  // param set — so a sort applied alongside a filter or search narrows and
  // orders together, and paging doesn't drop whichever of them is active.
  function buildInventoryQueryParams() {
    const values: KeyValueObj = {
      ...activeFilters,
      page,
      per_page: perPage,
    };

    if (searchTerm) {
      values.search_term = searchTerm;
    }

    if (sort) {
      values.sort_by = sort.sortBy;
      values.sort_direction = sort.direction;
    }

    return toParamsFormat(values);
  }

  function updateTableData() {
    setSearching(true);
    setListError(false);

    apiCall(
      `${INVENTORIES_URL}${buildInventoryQueryParams()}`,
      "GET",
      {},
      (data: any, metaData: any) => {
        setInventories(data ?? []);

        if (typeof metaData?.total_pages === "number") {
          setPagCount(metaData.total_pages);
        }

        if (data?.length === 0) {
          setTableData([]);
        }
      },
      () => setListError(true),
      () => setSearching(false),
      {
        dontNotifyOnSucess: true,
        dontNotifyonFail: true,
      },
    );
  }

  // MED-719: the one fetch path for this list — page, per_page, sort, active
  // filters and search all live here, so changing any one of them re-fetches
  // with all the others still applied. Must stay above the `if (loading)
  // return` below it — every hook in this component has to run on every
  // render regardless of `loading`, or React throws "rendered fewer hooks
  // than expected" the first time `loading` flips from true to false.
  useEffect(() => {
    updateTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sort, activeFilters, searchTerm, retry]);

  //FUNCTIONS

  function handleProductTypeSelected(type: string) {
    setProductType(type);
    if (type === "single_update") {
      setShowModal(true);
      setModalTitle("Add Inventory — Step 1 of 2");
      setStage("inventory-step1");
    }

    if (type === "bulk_upload") {
      setShowModal(false);
      setOpenBatchUpload(true);
    }
    setShowInventoryOptions(false);
  }

  const InvertoryTypeActions = [
    {
      label: "Single Upload",
      action: () => handleProductTypeSelected("single_update"),
    },
    {
      label: "Bulk Upload",
      action: () => handleProductTypeSelected("bulk_upload"),
    },
  ];

  function handleAddInventory(e: any) {
    setPosition(e);
    setShowInventoryOptions(true);
  }

  // MED-649: Step 1 -> Step 2 of the complete-batch inventory flow.
  function onInventoryStep1Continue(
    values: KeyValueObj,
    product: KeyValueObj,
    pricing: IIntakePricing,
  ) {
    setInventoryStep1Values(values);
    setInventoryPricing(pricing);
    setInventoryProduct(product);
    setServerErrorBySerial({});
    setModalTitle("Add Inventory — Step 2 of 2");
    setStage("inventory-step2");
  }

  function onInventoryStep2Back() {
    setModalTitle("Add Inventory — Step 1 of 2");
    setStage("inventory-step1");
  }

  function resetInventoryFlow() {
    setInventoryProduct(null);
    setInventoryStep1Values({});
    setInventoryPricing(EMPTY_INTAKE_PRICING);
    setReprice(null);
    setServerErrorBySerial({});
    setStage("inventory-step1");
  }

  // MED-649 §3.3 bulk-upload nudge: hand off to the existing bulk-upload
  // modal, pre-selecting the same product the actor was already on.
  function onSwitchToBulkUpload(productId: string) {
    setShowModal(false);
    resetInventoryFlow();
    setBatchFormData({ product_id: productId });
    setOpenBatchUpload(true);
  }

  // `keepExistingPrice` is the user declining the reprice warning: submit the
  // batch with no selling price so the product's current one stands.
  async function onInventorySubmit(
    tree: IInventoryNode,
    confirmReprice = false,
    keepExistingPrice = false,
  ) {
    setInventorySubmitLoading(true);
    const payload = {
      product_id: inventoryProduct?.id,
      ...inventoryStep1Values,
      // MED-686: pricing captured in Step 1. `selling_price_at_level` names the
      // price of ONE package at `selling_level` — the pairing the backend reads;
      // sending a per-carton figure under a `per_unit` key would be silently
      // mispriced by the containment factor.
      cost_price_per_unit: inventoryPricing.cost_price || undefined,
      selling_price_at_level: keepExistingPrice
        ? undefined
        : inventoryPricing.selling_price || undefined,
      // MED-659: the seller's selling level — the product's outermost defined
      // layer, per §3.4 of the SRS — unless the listing already names another.
      // Derived from the hierarchy rather than hardcoded to 1: positions are
      // 1-based today, but pinning a money conversion to a literal is exactly
      // the kind of thing that silently misprices if that ever stops being true.
      selling_level:
        inventoryPricing.selling_level ??
        inventoryProduct?.packaging_layers?.[0]?.position ??
        1,
      // The user's answer to the entire-product reprice warning. Absent on the
      // first attempt, so a price change is always refused once before it lands.
      ...(confirmReprice ? { confirm_reprice: true } : {}),
      inventory: toSubmitTree(tree),
    };

    const data = await apiCall(
      INVENTORY_URL,
      "POST",
      payload,
      () => {},
      (err: any) => {
        const body = err?.response?.data;

        // MED-686: the price would move the whole product. Nothing was written —
        // the server rolled its transaction back — so confirming re-submits the
        // identical payload and registers the stock exactly once.
        if (
          err?.response?.status === 409 &&
          body?.data?.reprice_confirmation_required
        ) {
          setReprice({ ...body.data, door: "single", tree, message: body?.message });
          return;
        }

        const apiErrors: KeyValueObj[] = body?.data?.errors || [];
        const bySerial: Record<string, string> = {};
        apiErrors.forEach((e) => {
          if (e.serial) bySerial[e.serial] = e.message;
        });
        setServerErrorBySerial(bySerial);
      },
      () => {
        setInventorySubmitLoading(false);
      },
      { dontNotifyOnSucess: true },
    );

    if (data?.inventory) {
      notify({
        type: "SUCCESS",
        message: "Inventory registered successfully",
      });
      setInventories([data.inventory, ...inventories]);
      resetInventoryFlow();
      setShowModal(false);
    }
  }

  // ************** filter batch data ************
  // MED-688: previously looked up in a separate `/products/dropdown` fetch
  // that duplicated this same approved-products list. `product_id` is always
  // chosen from `productOptions`, which is itself derived from
  // `productsFetched`, so the lookup always resolves here too.
  const selectedProductRaw = productsFetched?.find(
    (item: any) => item.id === BatchformData?.product_id,
  );
  // `name` mirrors ProductDropdownSerializer#name (brand + strength + unit,
  // e.g. "Amoxil 500 mg") rather than bare `brand_name`, so PreviewInventory's
  // "Product" field reads the same as it did off the old /products/dropdown
  // response.
  const selectedProduct = selectedProductRaw
    ? {
        ...selectedProductRaw,
        name: [
          selectedProductRaw.brand_name,
          selectedProductRaw.strength,
          selectedProductRaw.strength_unit,
        ]
          .filter((part) => part !== null && part !== undefined)
          .join(" "),
      }
    : undefined;

  // MED-646: download the per-product serialization template (dynamic per-level
  // columns) generated by the backend, replacing the static xlsx link.
  async function downloadSerializationTemplate(productId?: string) {
    if (!productId) return;
    setTemplateLoading(true);
    await apiCall(
      `${PRODUCT_URL}/${productId}/serialization-template`,
      "GET",
      {},
      (data: KeyValueObj) => {
        downloadBase64File(data.file_base64, data.filename, data.content_type);
      },
      () => {},
      () => {
        setTemplateLoading(false);
      },
    );
  }

  // The file plus whatever this pass needs. Built once because MED-686 sends the
  // SAME file twice — once to validate, once to commit with the prices — rather
  // than staging it server-side behind a token that would need expiry and
  // cleanup when someone closes the tab on the pricing modal.
  function buildBatchFormData(
    extras: KeyValueObj = {},
    confirmHeaderMismatch = false,
  ) {
    const formData = new FormData();
    Object.entries(BatchformData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });
    formData.append("product_name", selectedProduct?.name ?? "");
    if (selectedFile) formData.append("file", selectedFile);

    // MED-646 §4.3: header names differed but positions were valid — re-POST to
    // override after the user confirms.
    if (confirmHeaderMismatch)
      formData.append("confirm_header_mismatch", "true");

    Object.entries(extras).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, String(value));
      }
    });

    return formData;
  }

  // MED-686 pass 1. Nothing is written, so a file with bad rows costs the user a
  // list of rows to fix rather than a half-imported batch — and pricing is never
  // asked for against a file that was not going to import.
  async function onBatchValidate(confirmHeaderMismatch = false) {
    setValidating(true);
    setRowErrors(null);
    if (!confirmHeaderMismatch) setHeaderMismatch(null);

    await apiCall(
      BATCH_INVENTORY_VALIDATE_URL,
      "POST",
      buildBatchFormData({}, confirmHeaderMismatch),
      (data: KeyValueObj) => {
        setBatchValidation(data);
        setBatchPricing(EMPTY_INTAKE_PRICING);
        setHeaderMismatch(null);
        setStep("pricing");
      },
      (err: any) => {
        const body = err?.response?.data;
        if (err?.response?.status === 409 && body?.data?.header_mismatch) {
          setHeaderMismatch(body.data);
          return;
        }
        // Row-level problems and hierarchy mismatches both name what is wrong
        // with the file; keep the structured payload so the rows can be listed.
        if (body?.data?.error_code) {
          setRowErrors({ ...body.data, message: body?.message });
          return;
        }
        notify({ type: "ERROR", message: body?.message || "Could not validate the file" });
      },
      () => setValidating(false),
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    );
  }

  // MED-686 pass 2: the same file, now with the prices the modal collected.
  // `keepExistingPrice` is the user declining the reprice warning — the file
  // still imports, against the price the product already has.
  async function onBatchSubmit(confirmReprice = false, keepExistingPrice = false) {
    setSubmitLoading(true);

    const formData = buildBatchFormData(
      {
        cost_price: batchPricing.cost_price,
        selling_price: keepExistingPrice ? "" : batchPricing.selling_price,
        selling_level: batchPricing.selling_level ?? undefined,
        ...(confirmReprice ? { confirm_reprice: "true" } : {}),
      },
      // The header override, if the user already accepted it at validation, has
      // to ride along again — this is a fresh request over the same file.
      Boolean(headerMismatch) || Boolean(batchValidation?.confirmed_headers),
    );

    await apiCall(
      BATCH_INVENTORY_URL,
      "POST",
      formData,
      () => {
        setFormValues({});
        setHeaderMismatch(null);
        setReprice(null);
        setShowModal(false);
        setOpenBatchUpload(false);
        setRetry((prev: number) => prev + 1); // refresh the inventory table here
      },
      (err: any) => {
        const body = err?.response?.data;
        if (
          err?.response?.status === 409 &&
          body?.data?.reprice_confirmation_required
        ) {
          // Decided before any batch was imported, so confirming and re-posting
          // imports the file exactly once.
          setReprice({ ...body.data, door: "bulk", message: body?.message });
          return;
        }
        if (err?.response?.status === 409 && body?.data?.header_mismatch) {
          setHeaderMismatch(body.data);
          return;
        }
        notify({
          type: "ERROR",
          message: body?.message || "Upload failed",
        });
      },
      () => {
        setSubmitLoading(false);
      },
      { dontNotifyonFail: true },
    );
  }

  // The user declined the reprice warning: the upload still goes ahead, against
  // the price the product already has. Re-submitted immediately with the selling
  // price omitted, which is what the backend reads as "leave the price alone" —
  // it imports and stamps the batch from the existing listing.
  //
  // Omitting it is deliberate rather than sending the old figure back: the 409
  // reports prices per BASE UNIT, while the field is entered at the selling
  // level, so echoing that number into a level-scoped field would misprice the
  // product by the containment factor.
  function onDeclineReprice() {
    const declined = reprice;
    setReprice(null);
    if (!declined) return;

    if (declined.door === "bulk") {
      onBatchSubmit(false, true);
      return;
    }
    onInventorySubmit(declined.tree, false, true);
  }

  function onCloseModal() {
    setShowModal(false);
    setInventoryProduct(null);
    setInventoryStep1Values({});
    setServerErrorBySerial({});
  }

  function CloseBatuploadModal() {
    setStep("form");
    setSelectedFile(null);
    setHeaderMismatch(null);
    // MED-686: the validation result and the prices belong to the file that was
    // just abandoned; carrying them into the next upload would price a different
    // product against them.
    setBatchValidation(null);
    setBatchPricing(EMPTY_INTAKE_PRICING);
    setRowErrors(null);
    setReprice(null);
    setOpenBatchUpload(false);
  }

  if (loading) return <PageSkeletonLoader />;

  //  ************** Batch upload functions ***************
  const handleExternalUpdate = (name: string, value: any) => {
    setBatchFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
  // Previous search implementation kept here for reference
  function buildInventoryQueryParams(filters: KeyValueObj = {}) {
    const mergedFilters: KeyValueObj = {
      ...filters,
      page,
      per_page: perPage,
    };

    if (filters.search_term) {
      mergedFilters.search_term = filters.search_term;
    }

    const cleanedFilters = Object.entries(mergedFilters).reduce<KeyValueObj>(
      (acc, [key, value]: [string, any]) => {
        const isEmptyValue =
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "");

        if (isEmptyValue) {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    return toParamsFormat(cleanedFilters);
  }

  function updateTableData(queryParams: string) {
    apiCall(
      `${INVENTORY_URL}${queryParams}`,
      "GET",
      {},
      (data) => {
        const inventoryTable = (data || [])
          .slice()
          .reverse()
          .map((inventory: (typeof inventoriesFetched)[0]) => ({
            name: {
              value: inventory?.product?.brand_name,
              type: "string",
            },

            unit: {
              value: inventory?.product?.form,
              type: "string",
            },
            strength: {
              type: "string",
              value: `${inventory?.product?.strength}${
                inventory?.product?.strength_unit === "milligrams" ? "mg" : "g"
              }`,
            },
            storage: {
              value: inventory?.product.storage_requirement,
              type: "string",
            },

            quantity: {
              // MED-697: the flat per-batch table reads at the selling level
              // too. It is a different endpoint from the product-aggregated
              // view above, so it needs its own cell rather than inheriting one.
              value: (
                <QuantityCell
                  display={inventory?.quantity_display?.in_stock}
                  fallbackBaseUnits={inventory?.quantity_in_stock}
                />
              ),
              type: "string",
            },
            minLevel: {
              value: inventory?.reorder_level,
              type: "string",
            },
            supply: {
              value: (
                <QuantityCell
                  display={inventory?.quantity_display?.sold}
                  fallbackBaseUnits={inventory?.quantity_sold}
                />
              ),
              type: "string",
            },
            selling_price: {
              value: formatToNaira(inventory?.selling_price_per_unit),
              type: "string",
            },
            id: inventory?.id,
          }));

        inventoryTable && setTableData(inventoryTable);
      },
      () => {},
      () => {},
      { dontNotifyOnSucess: true },
    );
  }

  function handleSearch(value: string) {
    const trimmedValue = value?.trim();
    const nextFilters = { ...inventoryFilters };

    if (trimmedValue) {
      nextFilters.search_term = trimmedValue;
    } else {
      delete nextFilters.search_term;
    }

    setInventoryFilters(nextFilters);
    updateTableData(buildInventoryQueryParams(nextFilters));
  }

  function handleOrderFilter(values: KeyValueObj) {
    const normalizedValues = Object.entries(values).reduce<KeyValueObj>(
      (acc, [key, value]) => {
        if (value === undefined || value === null || value === "") {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    const nextFilters = { ...inventoryFilters, ...normalizedValues };

    setInventoryFilters(nextFilters);
    updateTableData(buildInventoryQueryParams(nextFilters));
  }
  */

  // A new search/filter/sort restarts pagination — continuing on whatever
  // page the previous view happened to be on would silently request a page
  // that may not exist under the new result set.
  function handleSearch(value: string) {
    setSearchTerm(value?.trim() ?? "");
    setPage(1);
  }

  function handleInventoryFilter(values: KeyValueObj) {
    const normalizedValues = Object.entries(values).reduce<KeyValueObj>(
      (acc, [key, value]) => {
        if (value === undefined || value === null || value === "") {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    setActiveFilters(normalizedValues);
    setPage(1);
  }

  function handleInventorySort(sortBy: string, direction: "asc" | "desc") {
    setSort({ sortBy, direction });
    setPage(1);
  }

  return (
    <>
      <AddInventoryWrapper>
        <PageTitleNavs
          title="Inventories"
          backLink="#"
          bcrumbs={[{ title: "Inventories", link: "#" }]}
        />
        <ProductFactory
          open={addMore}
          close={() => setAddMore(false)}
          productType={productType}
        />{" "}
        <div className="stats-wrapper">
          <div className="cards-wrapper">
            {getCardDetails(inventoryMetrics?.metrics)?.map(
              (card: ICardDetails, idx: number) => (
                <StatCard
                  key={idx}
                  title={card.title}
                  value={card.value}
                  imageUrl={card.imageUrl}
                  bg={card.bg}
                />
              ),
            )}
          </div>

          <div className="inventories ">
            <div className="controls">
              {(companyType?.toLowerCase() === "manufacturer" ||
                companyType?.toLowerCase() === "importer") && (
                <>
                  <Button
                    disabled={!hasFullAccess}
                    onClick={handleAddInventory}
                    title={
                      !hasFullAccess
                        ? "Locked until your company is approved and your PCN licence is valid"
                        : undefined
                    }
                  >
                    + Add Inventory
                  </Button>
                </>
              )}
            </div>
            <div className="table-wrapper">
              {/* MED-677: no `clickable` — the row's own three-dot menu
                  ("View" etc. below) is the single, consistent action entry
                  point; the separate navigation-arrow column read as "go
                  somewhere" rather than "act on this row". */}
              <Table
                tableHeaders={productInventoryTableHeaders}
                tableData={tableData}
                actionsData={[]}
                tableKeys={productInventoryTableKeys}
                full
                rowLineColor="light"
                loading={searching}
                errorLoading={listError}
                isSearching={searching}
                onRetry={() => setRetry((prev) => prev + 1)}
                onTRClick={(id) => navigate(`/dashboard/inventories/${id}`)}
                filterInputs={InventoryFilterOptions}
                handleFilter={handleInventoryFilter}
                handleSearch={handleSearch}
                sortFields={InventorySortFields}
                activeSortBy={sort?.sortBy}
                activeSortDirection={sort?.direction}
                onSort={handleInventorySort}
                showDropDown
                dropDownOptions={[
                  {
                    label: "View",
                    action: (row: KeyValueObj) =>
                      navigate(`/dashboard/inventories/${row?.id}`),
                  },
                  {
                    // MED-698: a manufacturer has no selling level to set — it
                    // is derived from the product's hierarchy — so the action
                    // must not offer one. The flag comes off the row payload,
                    // which the server derives from the seller, rather than
                    // from the logged-in company type here: the modal this
                    // opens reads the same server-sent flag, and the two
                    // deciding independently is how a menu ends up promising
                    // something the modal does not show.
                    // Short enough to sit on one line in the row menu, which is
                    // a fixed 12rem wide. "Set selling level & price" wrapped and
                    // then clipped; "selling" is redundant inside an inventory
                    // row where the only price is the selling one, and "level"
                    // is the word the modal this opens uses throughout, so the
                    // menu and the modal still name the same thing.
                    label: sellingLevelIsDerived
                      ? "Set price"
                      : "Set level & price",
                    action: (row: KeyValueObj) =>
                      openPricingModal(String(row?.id)),
                  },
                ]}
              />
              {/* MED-719: wires up the page/perPage state that already
                  existed but was never connected to any control — needed so
                  "sort holds as the user pages" is actually exercisable. */}
              <Pagination
                pageCount={pagCount}
                onNextPage={(event) => setPage(event.selected)}
                onPerPageChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              />
            </div>
          </div>

          <div className="mid-stats">
            <div className="bar">
              <h2 className="sub-title">Sales</h2>
              <Barchart
                chartData={getBarchartData(
                  inventoryMetrics?.sales_statistics?.monthly_breakdown,
                )}
                options={barOptions}
              />
            </div>
            <div className="short-table">
              <h2 className="sub-title">Recent Orders</h2>
              <Table
                tableHeaders={tableHeadersShort}
                tableData={getRecentOrderTableData(
                  inventoryMetrics?.recent_orders,
                )}
                actionsData={[]}
                tableKeys={tableKeysShort}
                full={false}
                rowLineColor="light"
              />
            </div>
          </div>
        </div>
        <Modal
          title={modalTitle}
          subTitle={modalSubtitle}
          isOpen={showModal}
          close={onCloseModal}
        >
          <ModalWrapper short={false}>
            {/* MED-649: complete-batch single upload, step 1 (batch details) */}
            <ModalFormWrapper show={stage === "inventory-step1"}>
              <InventoryStep1
                productOptions={productOptions}
                productsFetched={productsFetched}
                productsLoading={productsLoading}
                productsError={productsError}
                onRetry={() => setRetry((prev) => prev + 1)}
                onContinue={onInventoryStep1Continue}
                onSwitchToBulkUpload={onSwitchToBulkUpload}
                onDownloadTemplate={downloadSerializationTemplate}
                templateLoading={templateLoading}
              />
            </ModalFormWrapper>

            {/* MED-686: the entire-product reprice warning, raised by the server
                when the price entered in Step 1 differs from the product's
                current one. Nothing was written, so confirming re-submits the
                same tree and registers it once. */}
            <ModalFormWrapper show={stage === "inventory-step2" && reprice?.door === "single"}>
              {reprice?.door === "single" && (
                <RepriceConfirm
                  reprice={reprice as any}
                  message={reprice.message}
                  loading={inventorySubmitLoading}
                  onConfirm={() => onInventorySubmit(reprice.tree, true)}
                  onCancel={onDeclineReprice}
                />
              )}
            </ModalFormWrapper>

            {/* MED-649: step 2 (hierarchy-driven serial entry) */}
            <ModalWideWrapper show={stage === "inventory-step2" && !reprice}>
              {inventoryProduct && (
                // MED-675: keyed on the product so switching products (Step 2
                // -> Back -> pick a different product -> Continue, all
                // without the modal closing) fully remounts this component
                // instead of reusing its serial-tree state — without a key,
                // `inventoryProduct` never passes through null/unmount on a
                // straight product-to-product swap, so the tree's own
                // useState initializer never reruns.
                <InventoryStep2
                  key={inventoryProduct.id}
                  packagingLayers={inventoryProduct.packaging_layers}
                  submitLoading={inventorySubmitLoading}
                  serverErrorBySerial={serverErrorBySerial}
                  onBack={onInventoryStep2Back}
                  onSubmit={onInventorySubmit}
                />
              )}
            </ModalWideWrapper>
          </ModalWrapper>
        </Modal>
        {/* *********** Batch uploading section ************ */}
        <Modal
          isOpen={openBatchUpload}
          close={CloseBatuploadModal}
          title={step === "form" ? "Add Inventory" : "Preview Inventory"}
          subTitle={
            step === "form"
              ? "Upload Batch Serialization Data"
              : "Confirm Inventory Details"
          }
        >
          {step === "form" ? (
            /* ===================== FORM STEP ===================== */
            <div className="sm:w-[40rem] w-[30rem]">
              <div className="mx-10">
                <Select
                  prompt="Select the product"
                  placeholder="Select a product"
                  name="product_id"
                  options={productOptions}
                  fullWidth
                  shortWidth={false}
                  indexLevel={2}
                  returnId
                  formValues={BatchformData}
                  setFormValues={setBatchFormData}
                  updateData={handleExternalUpdate}
                />
              </div>

              <div className="p-3 m-5">
                <div className="mt-2 mb-2 flex justify-end">
                  {/* MED-646: template is generated per product (dynamic
                      per-level serial columns); enabled once a product is
                      selected. Replaces the static Batch-upload-template.xlsx. */}
                  <Button
                    type="button"
                    onClick={() =>
                      downloadSerializationTemplate(BatchformData?.product_id)
                    }
                    disabled={!BatchformData?.product_id || templateLoading}
                  >
                    {templateLoading ? "Preparing..." : "Download template"}
                  </Button>
                </div>

                <FileUpload onFileSelect={setSelectedFile} />
              </div>

              <div className="my-5 flex justify-center">
                <Button
                  onClick={() => setStep("preview")}
                  type="button"
                  disabled={!selectedFile || !BatchformData}
                >
                  Continue
                </Button>
              </div>
            </div>
          ) : headerMismatch ? (
            /* ============ HEADER-MISMATCH CONFIRMATION (§4.3) ============ */
            <div className="sm:w-[40rem] w-[30rem] mx-auto p-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                Column headers don&apos;t match this product&apos;s template
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                The columns are positionally valid, but their names differ. You
                can upload by position anyway.
              </p>
              <p className="text-sm mb-1">
                <span className="font-medium">Expected:</span>{" "}
                {headerMismatch.expected_columns?.join(", ")}
              </p>
              <p className="text-sm mb-4">
                <span className="font-medium">Got:</span>{" "}
                {headerMismatch.actual_columns?.join(", ")}
              </p>
              <div className="flex justify-between gap-3">
                <Button
                  type="button"
                  secondary
                  onClick={() => setHeaderMismatch(null)}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => onBatchValidate(true)}
                  disabled={validating}
                >
                  {validating ? "Checking..." : "Upload by position anyway"}
                </Button>
              </div>
            </div>
          ) : reprice?.door === "bulk" ? (
            /* ========= ENTIRE-PRODUCT REPRICE CONFIRMATION (MED-686) ========= */
            <RepriceConfirm
              reprice={reprice as any}
              message={reprice.message}
              loading={submitLoading}
              onConfirm={() => onBatchSubmit(true)}
              onCancel={onDeclineReprice}
            />
          ) : rowErrors ? (
            /* ============ FILE REJECTED BEFORE PRICING (MED-686) ============ */
            <div className="sm:w-[40rem] w-[30rem] mx-auto p-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                This file can&apos;t be imported yet
              </h3>
              <p className="text-sm text-gray-600 mb-3">{rowErrors.message}</p>

              {/* Every offending row at once: fixing a spreadsheet one error per
                  round trip is what makes bulk upload feel broken. */}
              {Array.isArray(rowErrors.errors) && rowErrors.errors.length > 0 && (
                <div className="mb-4 max-h-60 overflow-y-auto rounded border border-red-100 bg-red-50">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-red-100 text-red-900">
                      <tr>
                        <th className="px-3 py-2 font-medium">Row</th>
                        <th className="px-3 py-2 font-medium">Problem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowErrors.errors.map((rowError: KeyValueObj, idx: number) => (
                        <tr key={idx} className="border-t border-red-100">
                          <td className="px-3 py-1.5 align-top font-medium text-red-800">
                            {rowError.row}
                          </td>
                          <td className="px-3 py-1.5 text-red-700">{rowError.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-between gap-3">
                <Button type="button" secondary onClick={() => setRowErrors(null)}>
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => onBatchValidate(false)}
                  disabled={validating}
                >
                  {validating ? "Checking..." : "Check the file again"}
                </Button>
              </div>
            </div>
          ) : step === "pricing" ? (
            /* ============= PRICING STEP (MED-686) ============= */
            <div className="sm:w-[40rem] w-[30rem] mx-auto p-6">
              <p className="mb-3 rounded border border-green-100 bg-green-50 p-2.5 text-xs text-green-800">
                File checked:{" "}
                <span className="font-medium">
                  {batchValidation?.summary?.total_units} serial
                  {batchValidation?.summary?.total_units === 1 ? "" : "s"}
                </span>{" "}
                across {batchValidation?.summary?.total_batches} batch
                {batchValidation?.summary?.total_batches === 1 ? "" : "es"}. Nothing has
                been imported yet.
              </p>

              {BatchformData?.product_id && (
                <PricingDetails
                  productId={BatchformData.product_id}
                  value={batchPricing}
                  onChange={setBatchPricing}
                  disabled={submitLoading}
                />
              )}

              <div className="flex justify-between gap-3">
                <Button type="button" secondary onClick={() => setStep("preview")}>
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => onBatchSubmit(false)}
                  disabled={submitLoading || !batchPricing.complete}
                >
                  {submitLoading ? "Importing..." : "Import inventory"}
                </Button>
              </div>
            </div>
          ) : (
            /* ===================== PREVIEW STEP ===================== */
            <PreviewInventory
              data={selectedProduct}
              filePreview={selectedFile}
              packagingTypes={BatchformData?.package_type}
              onSubmit={() => onBatchValidate(false)}
              onBack={() => setStep("form")}
              loading={validating}
            />
          )}
        </Modal>
      </AddInventoryWrapper>

      <ActionPopup
        position={position}
        open={showInventoryOptions}
        onClose={() => setShowInventoryOptions(false)}
        actions={InvertoryTypeActions}
      />

      {/* MED-659 (UC-LST-01). Mounted only while open so each visit re-fetches
          the listing — the price may have changed elsewhere since the last one. */}
      {pricingProduct && (
        <SellingLevelPriceModal
          isOpen
          close={() => setPricingProduct(null)}
          productId={String(pricingProduct.product_id)}
          productName={pricingProduct.name}
          onSaved={handlePriceSaved}
          // MED-698: the grid already answered this for the row menu's label, so
          // hand it over rather than let the modal render a contradicting title
          // while its own fetch is still out.
          levelIsDerivedHint={sellingLevelIsDerived}
        />
      )}
    </>
  );
}
